import type { Middleware, UnknownAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

import { paymentCallbackCompleted, paymentSessionUsed } from "./payment/slice";
import { resetShoppingCart } from "./shoppingCart/slice";
import { initializePayment } from "./payment";
import {
  addProductToCart,
  bindContactInfo,
  bundleShoppingCart,
  deleteShoppingCart,
  initializeDiningCart,
  removeAllProductsFromCart,
  removeLinesFromCart,
  removeProductFromCart,
  unbundleShoppingCart,
} from "./shoppingCart";
import { logInWithID, setUser } from "./user";

type BreadcrumbMapping = {
  matcher: (action: UnknownAction) => boolean;
  message: string;
  category: string;
  level: "info" | "warning";
};

const breadcrumbMappings: BreadcrumbMapping[] = [
  // Cart operations
  {
    matcher: addProductToCart.fulfilled.match,
    message: "Product added to cart",
    category: "cart",
    level: "info",
  },
  {
    matcher: addProductToCart.rejected.match,
    message: "Failed to add product to cart",
    category: "cart",
    level: "warning",
  },
  {
    matcher: removeProductFromCart.fulfilled.match,
    message: "Product removed from cart",
    category: "cart",
    level: "info",
  },
  {
    matcher: removeLinesFromCart.fulfilled.match,
    message: "Cart lines removed",
    category: "cart",
    level: "info",
  },
  {
    matcher: removeAllProductsFromCart.fulfilled.match,
    message: "All products removed from cart",
    category: "cart",
    level: "info",
  },
  {
    matcher: resetShoppingCart.match,
    message: "Cart reset",
    category: "cart",
    level: "info",
  },
  {
    matcher: deleteShoppingCart.fulfilled.match,
    message: "Cart deleted",
    category: "cart",
    level: "info",
  },
  {
    matcher: initializeDiningCart.fulfilled.match,
    message: "Dining cart initialized",
    category: "cart",
    level: "info",
  },
  {
    matcher: initializeDiningCart.rejected.match,
    message: "Failed to initialize dining cart",
    category: "cart",
    level: "warning",
  },
  {
    matcher: bundleShoppingCart.fulfilled.match,
    message: "Cart bundled",
    category: "cart",
    level: "info",
  },
  {
    matcher: unbundleShoppingCart.fulfilled.match,
    message: "Cart unbundled",
    category: "cart",
    level: "info",
  },

  // Checkout & contact
  {
    matcher: bindContactInfo.fulfilled.match,
    message: "Contact info bound to cart",
    category: "checkout",
    level: "info",
  },
  {
    matcher: bindContactInfo.rejected.match,
    message: "Failed to bind contact info",
    category: "checkout",
    level: "warning",
  },

  // Payment
  {
    matcher: initializePayment.pending.match,
    message: "Payment initialization started",
    category: "payment",
    level: "info",
  },
  {
    matcher: initializePayment.fulfilled.match,
    message: "Payment initialized",
    category: "payment",
    level: "info",
  },
  {
    matcher: initializePayment.rejected.match,
    message: "Payment initialization failed",
    category: "payment",
    level: "warning",
  },
  {
    matcher: paymentSessionUsed.match,
    message: "Payment session used — redirecting to provider",
    category: "payment",
    level: "info",
  },
  {
    matcher: paymentCallbackCompleted.match,
    message: "Payment callback received",
    category: "payment",
    level: "info",
  },

  // User
  {
    matcher: logInWithID.fulfilled.match,
    message: "User logged in",
    category: "auth",
    level: "info",
  },
  {
    matcher: logInWithID.rejected.match,
    message: "User login failed",
    category: "auth",
    level: "warning",
  },
  {
    matcher: setUser.match,
    message: "User data set",
    category: "auth",
    level: "info",
  },
];

const addBreadcrumbForAction = (action: UnknownAction) => {
  for (const mapping of breadcrumbMappings) {
    if (mapping.matcher(action)) {
      Sentry.addBreadcrumb({
        category: mapping.category,
        message: mapping.message,
        level: mapping.level,
      });
      break;
    }
  }
};

const handleAction =
  (next: (action: unknown) => unknown) => (action: unknown) => {
    addBreadcrumbForAction(action as UnknownAction);
    return next(action);
  };

export const sentryBreadcrumbMiddleware: Middleware = () => handleAction;
